import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Table from "presentation/components/UI/Table";
import { orderMailboxUseCase } from "../../../di/container";
import type {
  OrderMailboxItem,
  CollectedEmailDetail,
  CollectedEmailStatus,
  CreateOrderFromEmailRequest,
} from "../../../domain/types/order-mailbox";
import { Button } from "presentation/components/UI/Button";
import { RefreshCw, Tag, User } from "lucide-react";
import Popup from "presentation/components/UI/Popup";
import { useNotifications } from "../../hooks/use-notifications";
import NotificationsList from "../../components/UI/NotificationList";
import CustomerEmailsBlock from "../../components/CompanyAdmin/OrderRecognitionRules/CustomerEmailsBlock";
import OrderKeywordsBlock from "../../components/CompanyAdmin/OrderRecognitionRules/OrderKeywordsBlock";
import { LoadingSpinner } from "presentation/components/UI/LoadingSpinner";
import MailOrderComposer from "../../components/CompanyAdmin/MailOrderComposer";

type RulePopup = "customerEmails" | "keywords" | null;

const PAGE_SIZE = 20;

const STATUS_BADGE_CLASS: Record<CollectedEmailStatus, string> = {
  COLLECTED: "bg-gray-100 text-gray-600",
  RECOGNIZED_AS_ORDER: "bg-blue-100 text-blue-600",
  ORDER_CREATED: "bg-green-100 text-green-700",
};

const STATUS_SHORT_LABEL: Record<CollectedEmailStatus, string> = {
  COLLECTED: "수집",
  RECOGNIZED_AS_ORDER: "인식됨",
  ORDER_CREATED: "생성됨",
};

const StatusBadge: React.FC<{
  status: CollectedEmailStatus;
}> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
      STATUS_BADGE_CLASS[status] ?? "bg-gray-100 text-gray-600"
    }`}
  >
    {STATUS_SHORT_LABEL[status] ?? status}
  </span>
);

const DetailField: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-gray-400 mb-1">{label}</span>
    <span className="text-gray-800 whitespace-pre-line">{value ?? "-"}</span>
  </div>
);

const DetailSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="font-semibold">{title}</h3>
    <div className="w-full grid grid-cols-2 gap-x-4 gap-y-2 rounded-[8px] bg-gray-50 p-4">
      {children}
    </div>
  </div>
);

const MailInbox: React.FC = () => {
  const { companyId } = useParams();
  const [items, setItems] = useState<OrderMailboxItem[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openPopup, setOpenPopup] = useState<RulePopup>(null);
  const [detail, setDetail] = useState<CollectedEmailDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /** 목록을 스크롤하는 컨테이너. IntersectionObserver 의 root 로 쓴다 */
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  /** 목록 끝에 두는 감시 대상. 이게 보이면 다음 페이지를 붙인다 */
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  /** 동시 요청 방지. state 로 두면 observer 콜백이 옛 값을 붙잡는다 */
  const isFetchingRef = useRef(false);

  const { notifications, addItem: addNotificationItem } = useNotifications();

  /** 한 페이지를 불러온다. 0 페이지면 갈아끼우고, 그 뒤면 이어 붙인다 */
  const fetchPage = useCallback(
    async (targetPage: number) => {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      setIsLoadingMore(true);
      try {
        const result = await orderMailboxUseCase.list(
          cid,
          undefined,
          undefined,
          targetPage,
          PAGE_SIZE,
        );
        setItems((prev) => {
          if (targetPage === 0) return result.items;
          // 수집으로 새 메일이 앞에 끼면 offset 이 밀려 이전 페이지 항목이 다시 넘어온다
          const seen = new Set(prev.map((item) => item.id));
          return [
            ...prev,
            ...result.items.filter((item) => !seen.has(item.id)),
          ];
        });
        setTotalPages(result.totalPages);
        setTotalElements(result.totalElements);
      } catch (error) {
        console.error("Error fetching order mailbox:", error);
      } finally {
        isFetchingRef.current = false;
        setIsLoadingMore(false);
      }
    },
    [companyId],
  );

  /** 목록을 처음부터 다시 쌓는다 (새 메일 수집, 주문 생성 후) */
  const reloadFromFirstPage = useCallback(async () => {
    setItems([]);
    if (page !== 0) setPage(0); // 페이지가 바뀌면 아래 effect 가 목록을 다시 부른다
    else await fetchPage(0);
  }, [page, fetchPage]);

  // 새 메일 확인 (동기 수집 후 목록 갱신)
  const handleRefresh = useCallback(async () => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    try {
      setIsRefreshing(true);
      await orderMailboxUseCase.refresh(cid);
      // 새로 들어온 메일은 맨 위에 붙으므로 처음부터 다시 쌓는다
      await reloadFromFirstPage();
    } catch (error) {
      console.error("Error refreshing order mailbox:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [companyId, reloadFromFirstPage]);

  // 상품/옵션 클릭 시 메일 상세 조회 후 모달 표시
  const handleOpenDetail = useCallback(
    async (id: number) => {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      try {
        setIsDetailLoading(true);
        const result = await orderMailboxUseCase.detail(cid, id);
        setDetail(result);
      } catch (error) {
        console.error("Error fetching order mailbox detail:", error);
      } finally {
        setIsDetailLoading(false);
      }
    },
    [companyId],
  );

  // 메일 상세에서 실제 주문 생성 (요청 본문은 MailOrderComposer 가 구성)
  const handleCreateOrder = useCallback(
    async (request: CreateOrderFromEmailRequest) => {
      const cid = Number(companyId);
      if (!Number.isFinite(cid) || !detail) return;

      try {
        setIsCreatingOrder(true);
        await orderMailboxUseCase.createOrder(cid, detail.id, request);
        addNotificationItem("주문이 생성되었습니다.");
        closeDetail();
        await reloadFromFirstPage();
      } catch (error) {
        console.error("Error creating order from mail:", error);
        addNotificationItem(
          error instanceof Error ? error.message : "주문 생성에 실패했습니다.",
        );
      } finally {
        setIsCreatingOrder(false);
      }
    },
    [companyId, detail, addNotificationItem, reloadFromFirstPage],
  );

  // 회사가 바뀌면 처음부터 다시 쌓는다
  useEffect(() => {
    setItems([]);
    setPage(0);
  }, [companyId]);

  useEffect(() => {
    fetchPage(page);
  }, [page, fetchPage]);

  const hasMore = page + 1 < totalPages;

  // 목록 끝이 보이면 다음 페이지를 이어 붙인다
  useEffect(() => {
    const rootEl = scrollContainerRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!rootEl || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || isFetchingRef.current) return;
        setPage((p) => (p + 1 < totalPages ? p + 1 : p));
      },
      // 스크롤되는 건 페이지가 아니라 안쪽 div 라서 root 를 직접 지정해야 한다
      { root: rootEl, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // items.length 를 넣어야, 목록이 화면보다 짧아 sentinel 이 계속 보이는
    // 상태에서도 다음 페이지가 이어서 붙는다
  }, [hasMore, totalPages, items.length]);

  const closePopup = () => setOpenPopup(null);
  const closeDetail = () => setDetail(null);

  return (
    <CompanyAdminLayout>
      <NotificationsList notifications={notifications} />
      <div className="h-full bg-gray-50 overflow-hidden flex flex-col">
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-5">
          <div className="mb-[28px] flex flex-col :justify-between gap-[20px] items-start md:items-start justify-between">
            <div className="w-full flex flex-col items-start gap-[0.3rem]">
              <h2 className="text-[1.375rem] font-semibold">주문 메일함</h2>
              <span className="text-[1rem] font-normal text-left">
                주문 메일을 조회하고 관리하세요.
              </span>
            </div>
            <div className="flex flex-row flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => setOpenPopup("customerEmails")}
              >
                <User size={16} className="mr-1" />
                등록된 고객 메일
              </Button>
              <Button
                variant="outline"
                onClick={() => setOpenPopup("keywords")}
              >
                <Tag size={16} className="mr-1" />
                주문 키워드
              </Button>
              <Button
                className="w-[130px]"
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw
                  size={16}
                  className={`mr-1 ${isRefreshing ? "animate-spin" : ""}`}
                />
                {isRefreshing ? "확인 중..." : "새 메일 확인"}
              </Button>
            </div>
          </div>
          <div className="text-[14px]">
            <Table
              tabledata={{
                head: [
                  <>주문자</>,
                  <>상품/옵션</>,
                  <>상태</>,
                  <>연락처</>,

                  <>개수</>,
                  <>납기일</>,
                  <>받은 시각</>,
                ],
                body: items.map((item) => [
                  <>
                    <div className="flex items-center gap-3 w-full">
                      <div className="rounded-full w-10 h-10 bg-[#E7EFFF] text-[#4185F4] flex items-center justify-center font-bold aspect-square">
                        {item.sender.name?.charAt(0) ?? "-"}
                      </div>
                      <div className="flex flex-col mr-3 w-full">
                        <div>{item.sender.name ?? "-"}</div>
                        <div>{item.sender.emailAddress}</div>
                      </div>
                    </div>
                  </>,
                  <>
                    <button
                      type="button"
                      onClick={() => handleOpenDetail(item.id)}
                      className="text-[#4185F4] underline text-left"
                    >
                      {item.order.productName ?? item.mail.mailSubject ?? "-"}
                    </button>
                  </>,
                  <>
                    <StatusBadge status={item.mail.status} />
                  </>,
                  <>{item.sender.phone ?? "-"}</>,

                  <>{item.order.quantity ?? "-"}</>,
                  <>{item.order.dueDate ?? "-"}</>,
                  <>
                    {item.mail.receivedAt?.replace("T", " ").slice(0, 16) ??
                      "-"}
                  </>,
                ]),
              }}
              columnWidths={[
                "270px",
                "250px",
                "90px",
                "180px",
                "100px",
                "130px",
                "180px",
              ]}
            />

            {/* 무한 스크롤: 목록 끝에 닿으면 다음 페이지를 이어 붙인다 */}
            {totalElements > 0 && (
              <div className="mt-4 flex flex-col items-center gap-2">
                <div ref={loadMoreSentinelRef} className="h-px w-full" />
                {isLoadingMore ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <span className="text-[13px] text-[#888]">
                    총 {totalElements}건 중 {items.length}건 표시
                    {!hasMore && " (마지막)"}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 주문 인식 규칙 블럭 팝업 */}
      <Popup isOpen={openPopup === "customerEmails"} onClose={closePopup}>
        <CustomerEmailsBlock onNotify={addNotificationItem} variant="plain" />
      </Popup>
      <Popup isOpen={openPopup === "keywords"} onClose={closePopup}>
        <OrderKeywordsBlock onNotify={addNotificationItem} variant="plain" />
      </Popup>

      {/* 메일 상세 모달 (좌: 메일 원본 / 우: 주문 구성) */}
      <Popup
        isOpen={detail !== null || isDetailLoading}
        onClose={closeDetail}
        className="max-w-[1200px]"
      >
        {isDetailLoading && (
          <div className="py-10 text-center text-gray-500">
            <LoadingSpinner />
          </div>
        )}
        {detail && (
          <div className="text-start grid grid-cols-1 lg:grid-cols-2 gap-6 text-[14px]">
            {/* 좌측: 메일 원본 */}
            <div className="flex flex-col gap-5 lg:border-r lg:border-gray-200 lg:pr-6">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-[1.25rem] font-semibold">
                    {detail.mail.mailSubject ?? "-"}
                  </h2>
                  <StatusBadge status={detail.mail.status} />
                </div>
                <span className="text-gray-500">
                  {detail.sender.name ? `${detail.sender.name} · ` : ""}
                  {detail.sender.emailAddress}
                </span>
                <span className="text-gray-400">
                  {detail.mail.receivedAt?.replace("T", " ").slice(0, 16) ??
                    "-"}
                </span>
              </div>

              <DetailSection title="AI 추출 요약">
                <DetailField
                  label="상품/옵션"
                  value={detail.order.productName}
                />
                <DetailField
                  label="개수"
                  value={detail.order.quantity?.toString() ?? null}
                />
                <DetailField label="납기일" value={detail.order.dueDate} />
                <DetailField label="수령인" value={detail.recipient.name} />
                <DetailField
                  label="수령인 연락처"
                  value={detail.recipient.phone}
                />
                <DetailField
                  label="배송지"
                  value={
                    [
                      detail.address.postcode && `(${detail.address.postcode})`,
                      detail.address.address1,
                      detail.address.address2,
                    ]
                      .filter(Boolean)
                      .join("\n") || null
                  }
                />
              </DetailSection>

              <div className="flex flex-col gap-2">
                <h3 className="font-semibold">메일 본문</h3>
                {detail.mail.bodyHtml ? (
                  // sandbox="" 로 스크립트/폼/팝업을 전면 차단해 XSS-safe 하게 HTML 을 렌더링한다.
                  <iframe
                    srcDoc={detail.mail.bodyHtml}
                    sandbox=""
                    className="w-full h-[45vh] rounded border border-[#eee] bg-white"
                    title="메일 본문"
                  />
                ) : (
                  <div className="whitespace-pre-wrap rounded-[8px] border border-gray-200 p-4 text-gray-700 max-h-[45vh] overflow-y-auto">
                    {detail.mail.body || "(본문 없음)"}
                  </div>
                )}
              </div>
            </div>

            {/* 우측: 주문 구성 (AI 매칭 프리셀렉트) */}
            <div>
              {Number.isFinite(Number(companyId)) && (
                <MailOrderComposer
                  key={detail.id}
                  companyId={Number(companyId)}
                  detail={detail}
                  isCreatingOrder={isCreatingOrder}
                  onCreateOrder={handleCreateOrder}
                  onNotify={addNotificationItem}
                />
              )}
            </div>
          </div>
        )}
      </Popup>
    </CompanyAdminLayout>
  );
};

export default MailInbox;
